<script setup>
/**
 * Сводные результаты оценок для администратора (role 100):
 * жюри → результаты по команде → шаблон критериев (проверка наличия задачи).
 */
import { useStore } from "vuex";
import { ref, onMounted, computed } from "vue";
import axios from "axios";

const store = useStore();

const results = ref([]);
const users = ref([]);
/** Структура критериев с сервера (как в TheCriteria); для валидации NOT_FOUND */
const criteria = ref([]);

const isInitializing = ref(true);
/** PARAMS | NETWORK | DATA */
const loadError = ref(null);
const taskError = ref(false);

const token = computed(() => store.getters["authModule/token"]);

// eslint-disable-next-line no-undef
const props = defineProps({
	teamId: { type: String, required: true },
	userId: { type: String, required: true },
	taskId: { type: String, required: true },
});

const useCommonCriteriaList = () =>
	Number(process.env.VUE_APP_CRITERIAS_TYPE) === 0;

function criteriaListUrl() {
	const base = process.env.VUE_APP_ADMIN_URL + "/api/data/";
	if (useCommonCriteriaList()) {
		return base + "criteriaList.php";
	}
	return `${base}criteriaList_${props.taskId}.php`;
}

onMounted(() => {
	void init();
});

function resetState() {
	loadError.value = null;
	taskError.value = false;
	results.value = [];
	users.value = [];
	criteria.value = [];
}

async function init() {
	resetState();
	isInitializing.value = true;

	if (!String(props.teamId || "").trim() || !String(props.userId || "").trim()) {
		loadError.value = "PARAMS";
		isInitializing.value = false;
		return;
	}

	if (!useCommonCriteriaList() && !String(props.taskId || "").trim()) {
		taskError.value = true;
		isInitializing.value = false;
		return;
	}

	const admin = process.env.VUE_APP_ADMIN_URL + "/api";
	const tok = encodeURIComponent(token.value || "");
	const team = encodeURIComponent(props.teamId);

	try {
		const { data: usersRaw } = await axios.get(
			`${admin}/users.php?token=${tok}`,
		);
		if (!Array.isArray(usersRaw)) {
			console.error("TheCriteriaAll: users.php ожидался массив", usersRaw);
			loadError.value = "DATA";
			return;
		}
		users.value = usersRaw;

		const { data: resultsRaw } = await axios.get(
			`${admin}/results.php?teamId=${team}&token=${tok}`,
		);
		if (!Array.isArray(resultsRaw)) {
			console.error("TheCriteriaAll: results.php ожидался массив", resultsRaw);
			loadError.value = "DATA";
			return;
		}
		results.value = resultsRaw;

		const listUrl = criteriaListUrl();
		const { data: listRaw } = await axios.get(listUrl);

		if (listRaw && typeof listRaw === "object" && !Array.isArray(listRaw)) {
			if (listRaw.error === "NOT_FOUND") {
				taskError.value = true;
				criteria.value = [];
				return;
			}
			console.error("TheCriteriaAll: неожиданный объект criteriaList", listRaw);
			loadError.value = "DATA";
			return;
		}

		if (!Array.isArray(listRaw)) {
			console.error("TheCriteriaAll: criteriaList ожидался массив", listRaw);
			loadError.value = "DATA";
			return;
		}
		criteria.value = listRaw;
	} catch (e) {
		console.error("TheCriteriaAll init:", e);
		loadError.value = "NETWORK";
		results.value = [];
		users.value = [];
		criteria.value = [];
	} finally {
		isInitializing.value = false;
	}
}

/**
 * HTML-блок оценок одного члена жюри (как в старой версии: «Всего» после каждой
 * записи результата с накопленной суммой); при битых данных не падает.
 */
const createResult = (id) => {
	let result = "";
	let summa = 0;

	if (!Array.isArray(results.value)) {
		return '<h4 class="text-danger">Нет данных</h4>';
	}

	for (const item of results.value) {
		if (String(item.userId) !== String(id)) {
			continue;
		}
		if (!Array.isArray(item.result)) {
			continue;
		}

		result += "<p>" + (item.created || "") + "</p>";

		for (let i = 0; i < item.result.length; i++) {
			const block = item.result[i];
			if (!block || !Array.isArray(block.sections)) {
				continue;
			}
			result += "<h4>" + (block.title || "") + "</h4>";

			for (let j = 0; j < block.sections.length; j++) {
				const section = block.sections[j];
				summa += Number(section.activeIndex) || 0;
				result += '<div class="d-flex justify-content-between">';
				result += '<div class="w-75">' + (section.name || "") + "</div>";
				result += "<h2>" + (section.activeIndex ?? "") + "</h2>";
				result += "</div>";
				result += "<hr>";
			}
		}

		result += '<div class="d-flex justify-content-between">';
		result += "<h2>Всего</h2>";
		result += '<h2 class="text-danger">' + summa + "</h2>";
		result += "</div>";
	}

	if (summa === 0) {
		result = '<h4 class="text-danger">Нет оценок</h4>';
	}

	return result;
};

const total = () => {
	if (!Array.isArray(results.value)) {
		return 0;
	}
	let summa = 0;
	for (const item of results.value) {
		if (!Array.isArray(item.result)) {
			continue;
		}
		for (let i = 0; i < item.result.length; i++) {
			const block = item.result[i];
			if (!block || !Array.isArray(block.sections)) {
				continue;
			}
			for (let j = 0; j < block.sections.length; j++) {
				const idx = Number(block.sections[j].activeIndex);
				if (!Number.isNaN(idx)) {
					summa += idx;
				}
			}
		}
	}
	return summa;
};
</script>

<template>
	<div v-if="isInitializing" class="d-flex justify-content-center py-5">
		<div class="spinner-border text-primary" role="status"></div>
	</div>

	<div v-else-if="loadError === 'NETWORK'" class="alert alert-danger" role="alert">
		Не удалось загрузить данные. Проверьте соединение и обновите страницу.
	</div>
	<div v-else-if="loadError === 'PARAMS'" class="alert alert-danger" role="alert">
		Некорректные параметры страницы. Вернитесь к карточке команды.
	</div>
	<div v-else-if="loadError === 'DATA'" class="alert alert-danger" role="alert">
		Ответ сервера имеет неверный формат. Обратитесь к администратору.
	</div>

	<div v-else-if="taskError">
		<div class="alert alert-danger">
			<h5>Что-то пошло не так...</h5>
			Список критериев для оценки данной команды не найден
		</div>
	</div>

	<div v-else>
		<h3 class="text-center">Результаты оценки команды</h3>

		<p v-if="users.length === 0" class="text-center text-muted mt-3">
			Нет членов жюри для отображения.
		</p>

		<div
			v-for="(item, index) in users"
			:key="item.id != null ? String(item.id) : index"
			class="card mt-4"
		>
			<div class="card-header bg-info">
				<h4 class="card-title">{{ item["name"] }}</h4>
			</div>
			<div class="card-body" v-html="createResult(item['id'])"></div>
		</div>

		<h3 v-if="total() > 0" class="text-center mt-4">
			Общая сумма баллов: {{ total() }}
		</h3>
	</div>
</template>

<style scoped></style>
